import { Link } from 'react-router-dom';
import { MapPin, Camera, Clock, Users, Bus, Smartphone, CheckCircle, TrendingUp, Star, MessageCircle, Mountain, Building2 } from 'lucide-react';
// @ts-ignore: module has no declaration file
import useDynamicSEO from '../hooks/dynamicSEO';

const TouristAttractionParkingSoftware = () => {
    useDynamicSEO({
      title: "Tourist Attraction Parking Software | High-Crowd Automation",
      description: "Handle seasonal surges & crowd pressure with smart Tourist Attraction Parking Software. Improve visitor experience with automated flow & revenue management.",
          keywords:["Parking Management System","Event & Exhibition Parking Management","Parking Lot Management App","Digital Parking Solutions"," Vehicle entry exit management system","AI Parking Management Platform","Parking Lot Automation Software", "Parking revenue management app", "Tourist Attraction Parking Software", "Cinema & Multiplex Parking System"],
      ogTitle: "Tourist Attraction Parking Software | High-Crowd Automation",
      ogDescription: "Handle seasonal surges & crowd pressure with smart Tourist Attraction Parking Software. Improve visitor experience with automated flow & revenue management."
    });

  const handleWhatsAppClick = () => {
    const phoneNumber = '9384376329';
    const message = encodeURIComponent('Hi, I would like to request a demo of Park Hive Tourist Attraction Parking Software for managing high-crowd locations and seasonal visitor surges.');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const touristChallenges = [
    "Sudden volume surges within minutes",
    "Seasonal spikes and festival crowds",
    "Tour buses and large vehicles needing coordination",
    "Visitors unfamiliar with site layout",
    "Limited staff availability",
    "Cash management struggles and revenue leakage",
    "Difficulties in manually tracking entry, exit, and stay duration"
  ];

  const touristFeatures = [
    {
      icon: <Camera className="w-8 h-8 text-blue-600" />,
      title: "Instant Vehicle Scanning",
      description: "Rapid entry & exit for all vehicle types including buses, cars, bikes, and tourist fleets"
    },
    {
      icon: <Clock className="w-8 h-8 text-green-600" />,
      title: "Automated Time & Pricing",
      description: "Automatic tracking and price calculation without manual intervention or delays"
    },
    {
      icon: <Users className="w-8 h-8 text-purple-600" />,
      title: "Real-time Occupancy Status",
      description: "Live monitoring to prevent congestion and guide visitors to available spaces"
    },
    {
      icon: <Bus className="w-8 h-8 text-orange-600" />,
      title: "Multi-vehicle Support",
      description: "Handles buses, vans, cars, bikes, and specialized tourist transportation"
    },
    {
      icon: <Smartphone className="w-8 h-8 text-indigo-600" />,
      title: "Digital Payment Integration",
      description: "Fast, clean financial transactions that eliminate cash handling delays"
    },
    {
      icon: <Building2 className="w-8 h-8 text-red-600" />,
      title: "Multi-gate Management",
      description: "Control multiple entry/exit points and parking zones from single dashboard"
    }
  ];

  const touristSites = [
    "Historic monuments & temples",
    "Museums, theme parks & entertainment complexes",
    "Beaches, hill stations & nature reserves", 
    "Wildlife safaris & adventure parks",
    "Seasonal fairs, festivals & cultural events",
    "Tourism authority parking and smart city clusters"
  ];

  const businessBenefits = [
    "Faster vehicle turnover and shorter waiting time",
    "Increased visitor satisfaction and destination reputation",
    "Stronger financial control and verified revenue",
    "Reduced manpower dependency",
    "Organized parking that supports safety and order",
    "Ability to scale during season peaks without panic"
  ];

  const smartParkingReasons = [
    "Memories begin at the gate",
    "Bad parking becomes a public review instantly",
    "Today's visitor expects efficiency, not excuses",
    "Transparent revenue protects public trust",
    "Digital transformation is no longer optional"
  ];

  const faqData = [
    {
      question: "Can this work for temporary tourist events and peak seasons?",
      answer: "Yes — setup takes minutes and scales smoothly."
    },
    {
      question: "Does it support multiple gates and multiple parking zones?",
      answer: "Absolutely — operate all gates from one dashboard."
    },
    {
      question: "Can this reduce staffing requirements?",
      answer: "Yes — automation handles most manual tasks."
    },
    {
      question: "What about connectivity issues in remote tourist areas?",
      answer: "Offline functionality ensures continuity."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 via-purple-800 to-indigo-900 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <Mountain className="w-16 h-16 text-blue-300" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Tourist Attraction Parking Software
          </h1>
          <p className="text-2xl md:text-3xl mb-8 text-blue-200">
            Smart Parking for Tourist Attractions, High-Crowd Locations & Seasonal Visitor Surges
          </p>
          <p className="text-xl mb-10 max-w-4xl mx-auto">
            Transform uncontrolled visitor flow into smooth, predictable movement with crowd-ready automation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleWhatsAppClick}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Request Live Demo
            </button>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 text-center"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* First Impression Problem */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              The Experience at the Gate Defines the Experience Inside
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              Tourist destinations thrive on experience — but the very first experience every tourist faces isn't the view or the adventure. It's the parking lot.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">The Parking Reality</h3>
              <p className="text-gray-700 mb-6">
                When parking is messy, slow, and chaotic, frustration overshadows excitement even before the journey begins. 
                Long queues, honking, confusion, arguments, lost tickets, cash mismatches, angry families, delayed tour buses — 
                all of it damages the reputation of a tourist destination instantly.
              </p>
              <div className="bg-red-50 p-6 rounded-lg">
                <p className="text-red-800 font-semibold">
                  First impressions matter. Parking is that first impression.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-blue-600 mb-4">The Park Hive Solution</h3>
              <p className="text-gray-700 mb-6">
                Park Hive ensures entry and exit are fast, coordinated, and transparent — making the journey smooth from the very first moment.
              </p>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <Star className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="font-bold text-blue-800">Fast Entry</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="font-bold text-green-800">Smooth Flow</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p className="font-bold text-purple-800">Happy Visitors</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <Users className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                  <p className="font-bold text-yellow-800">Crowd Ready</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unique Challenges */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Makes Tourist Parking Uniquely Challenging
            </h2>
            <p className="text-xl text-gray-700">
              Tourist locations face problems that regular parking lots never see
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Complex Challenges</h3>
              <div className="space-y-4">
                {touristChallenges.map((challenge, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{challenge}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 bg-red-50 p-6 rounded-lg">
                <p className="text-red-800 font-semibold">
                  Traditional ticketing collapses under this load — creating traffic jams, safety risks, and reputation damage.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-blue-800 mb-6">Digital Solution</h3>
              <p className="text-gray-700 mb-6">
                Crowds don't always grow gradually — they explode during weekends, holidays, and peak seasons. 
                Manual parking systems simply cannot survive that pressure.
              </p>
              <div className="bg-white p-6 rounded-lg mb-6">
                <p className="font-bold text-center text-gray-900">
                  Busy tourist hubs require powerful, automated, crowd-ready software that handles traffic spikes with intelligence, accuracy, and speed.
                </p>
              </div>
              <p className="text-blue-800 font-semibold text-center">
                A digital parking solution handles this pressure effortlessly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Park Hive Solves Crowd Pressure */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              How Park Hive Solves Crowd Pressure at Tourist Attractions
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              Every visitor's journey becomes fast, predictable, and stress-free with intelligent automation
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-16">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Instant Check-in</h3>
              <p className="text-gray-600 text-sm">Smart digital scanning for immediate vehicle entry</p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Auto Tracking</h3>
              <p className="text-gray-600 text-sm">Time tracking begins automatically without manual logs</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Digital Payment</h3>
              <p className="text-gray-600 text-sm">Fast transactions without cash handling delays</p>
            </div>

            <div className="text-center">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Smooth Exit</h3>
              <p className="text-gray-600 text-sm">Organized flow even when thousands leave at once</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">
              Live Operational Control
            </h3>
            <p className="text-lg text-gray-700 text-center max-w-4xl mx-auto mb-8">
              Behind the scenes, owners and operators monitor everything live — number of vehicles parked, stay durations, 
              peak periods, total collection, and staff activity.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center bg-blue-50 p-4 rounded-lg">
                <p className="font-bold text-blue-800">Faster Flow</p>
                <p className="text-blue-600 text-sm">Minimal waiting</p>
              </div>
              <div className="text-center bg-green-50 p-4 rounded-lg">
                <p className="font-bold text-green-800">Happy Visitors</p>
                <p className="text-green-600 text-sm">Smiling arrivals</p>
              </div>
              <div className="text-center bg-purple-50 p-4 rounded-lg">
                <p className="font-bold text-purple-800">Traceable Revenue</p>
                <p className="text-purple-600 text-sm">Complete transparency</p>
              </div>
              <div className="text-center bg-yellow-50 p-4 rounded-lg">
                <p className="font-bold text-yellow-800">Zero Confusion</p>
                <p className="text-yellow-600 text-sm">No arguments, no loss</p>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-r from-green-600 to-blue-600 text-white p-6 rounded-lg">
              <p className="text-xl font-bold text-center">
                Tourist parking becomes an organized operation instead of a daily survival battle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features for Tourist Attractions */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Features Tailored for Tourist Attractions
            </h2>
            <p className="text-xl text-gray-700">
              Complete support for large-scale public parking with tourism complexity
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {touristFeatures.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-700">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Advanced Tourism Features</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Staff access permissions for seasonal teams</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Offline capability for remote regions</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">PDF reports for government compliance</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Amenities listing (CCTV, EV charging, valet, washrooms)</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Designed for High Volume</h3>
              <p className="text-gray-700 mb-6">
                Software that adapts to tourism complexity — not the other way around.
              </p>
              <div className="bg-white p-6 rounded-lg">
                <p className="font-semibold text-center text-purple-800">
                  Wherever travellers gather, movement must be smooth — or problems multiply instantly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tourist Site Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Designed for High-Volume Tourist Sites
            </h2>
            <p className="text-xl text-gray-700">Perfect for every type of tourist destination</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {touristSites.map((site, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
                <MapPin className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900">{site}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Impact */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              The Business Impact for Parking Owners
            </h2>
            <p className="text-xl text-gray-700">
              Automated parking transforms tourist location operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {businessBenefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <p className="text-gray-700">{benefit}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 rounded-xl text-center">
            <h3 className="text-2xl font-bold mb-4">Economic Value Chain</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-xl font-bold mb-2">Better Parking</p>
                <p className="text-green-200">leads to</p>
                <p className="text-xl font-bold">Better Tourism</p>
              </div>
              <div>
                <p className="text-xl font-bold mb-2">Better Tourism</p>
                <p className="text-blue-200">leads to</p>
                <p className="text-xl font-bold">Higher Economic Value</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Smart Parking Matters */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              Why Smart Parking Matters for Tourism Destinations
            </h2>
            <p className="text-xl text-gray-300">
              Because digital transformation is no longer optional
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6 mb-16">
            {smartParkingReasons.map((reason, index) => (
              <div key={index} className="bg-white bg-opacity-10 p-6 rounded-lg text-center">
                <Star className="w-8 h-8 mx-auto mb-4 text-yellow-400" />
                <p className="font-semibold">{reason}</p>
              </div>
            ))}
          </div>

          <div className="bg-white bg-opacity-10 p-8 rounded-xl text-center">
            <h3 className="text-2xl font-bold mb-6">Complete Transformation</h3>
            <p className="text-xl mb-8">
              Park Hive empowers operators to turn chaos into coordination and stress into satisfaction.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-red-500 bg-opacity-20 p-4 rounded-lg">
                <p className="font-bold">From: Chaos & Stress</p>
              </div>
              <div className="bg-green-500 bg-opacity-20 p-4 rounded-lg">
                <p className="font-bold">To: Coordination & Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Experience Tourism Parking Powered by Intelligence
          </h2>
          <p className="text-xl mb-8">
            See Park Hive in action. Watch crowd movement transform from unpredictable to perfectly controlled.
          </p>
          <button 
            onClick={handleWhatsAppClick}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition duration-300 flex items-center justify-center gap-2 mx-auto"
          >
            <MessageCircle className="w-5 h-5" />
            Request a Demo - Make Every Visitor's Journey Unforgettable
          </button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-700">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Message */}
      <section className="py-16 bg-gradient-to-r from-blue-800 to-purple-800 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Tourism is built on experience — and experience begins at the parking gate.
          </h2>
          <div className="space-y-4 text-lg mb-8">
            <p>When parking works smoothly, everything that follows feels magical.</p>
            <p>A successful destination is not only about attractions —</p>
            <p className="font-bold text-yellow-400">it's about how easily people arrive.</p>
          </div>
          <div className="bg-white bg-opacity-20 p-6 rounded-xl">
            <p className="text-2xl font-bold">
              Park Hive makes that journey seamless.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TouristAttractionParkingSoftware;